import React from "react";
import { Handle, Position } from "reactflow";
import BooleanSvg from "../../../svgs/parameter-type-icons/boolean.svg";
import DateTimeOffsetSvg from "../../../svgs/parameter-type-icons/date-time-offset.svg";
import DateTimeSvg from "../../../svgs/parameter-type-icons/date-time.svg";
import DateSvg from "../../../svgs/parameter-type-icons/date.svg";
import DecimalSvg from "../../../svgs/parameter-type-icons/decimal.svg";
import DoubleSvg from "../../../svgs/parameter-type-icons/double.svg";
import FileContentSvg from "../../../svgs/parameter-type-icons/file-content.svg";
import GuidSvg from "../../../svgs/parameter-type-icons/guid.svg";
import ImageSvg from "../../../svgs/parameter-type-icons/image.svg";
import Int16Svg from "../../../svgs/parameter-type-icons/int16.svg";
import Int32Svg from "../../../svgs/parameter-type-icons/int32.svg";
import Int64Svg from "../../../svgs/parameter-type-icons/int64.svg";
import LocationSvg from "../../../svgs/parameter-type-icons/location.svg";
import StringSvg from "../../../svgs/parameter-type-icons/string.svg";
import { BASE_ENTITY_COLUMNS, sortTableColumns } from "./table-card-geometry";

// Extracted regex to prevent recreation on each render
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Helper function to get type icon
export const getTypeIcon = (type: string) => {
  switch (type?.toLowerCase()) {
    case "unknown":
      return <StringSvg />;
    case "boolean":
      return <BooleanSvg />;
    case "bytea":
    case "byte":
      return <DoubleSvg />;
    case "date":
      return <DateSvg />;
    case "datetime":
      return <DateTimeSvg />;
    case "location":
      return <LocationSvg />;
    case "filecontent":
      return <FileContentSvg />;
    case "datetimeoffset":
    case "timestamp with time zone":
      return <DateTimeOffsetSvg />;
    case "decimal":
    case "numeric":
      return <DecimalSvg />;
    case "double":
    case "double precision":
    case "float":
    case "varbinary":
    case "single":
      return <DoubleSvg />;
    case "guid":
    case "uniqueidentifier":
      return <GuidSvg />;
    case "int16":
    case "smallint":
      return <Int16Svg />;
    case "int32":
    case "int":
      return <Int32Svg />;
    case "int64":
    case "bigint":
      return <Int64Svg />;
    case "string":
    case "nvarchar":
      return <StringSvg />;
    case "image":
      return <ImageSvg />;
    case "bit":
      return <BooleanSvg />;
    default:
      return <StringSvg />;
  }
};

export interface TableNodeProps {
  data: any;
}

// Custom Table Node Component
// Using PureComponent to prevent unnecessary re-renders
// Hover effects handled via CSS to avoid React re-renders
export class TableNode extends React.PureComponent<TableNodeProps> {
  private readonly baseEntityColumns = BASE_ENTITY_COLUMNS;

  private sortedColumnsCache: any[] | null = null;

  private lastColumnsRef: any[] | null = null;

  shouldComponentUpdate(nextProps: TableNodeProps) {
    const { data } = this.props;
    const nextData = nextProps.data;
    return (
      data.table !== nextData.table ||
      data.sourceHandles !== nextData.sourceHandles ||
      data.targetHandles !== nextData.targetHandles
    );
  }

  getBaseEntityType = (columnName: string): string => {
    switch (columnName) {
      case "CreatedAt":
        return "DateTimeOffset";
      case "CreatedUser_Id":
        return "Guid";
      case "IsDeleted":
        return "Boolean";
      default:
        return "String";
    }
  };

  getSortedColumns = () => {
    const { data } = this.props;
    const columns = data.table.columns || [];

    if (this.sortedColumnsCache && this.lastColumnsRef === columns) {
      return this.sortedColumnsCache;
    }

    this.lastColumnsRef = columns;
    this.sortedColumnsCache = sortTableColumns(columns);

    return this.sortedColumnsCache;
  };

  private columnsCache: React.ReactNode[] | null = null;

  private lastRenderData: any = null;

  renderColumns = () => {
    const { data } = this.props;

    if (
      this.columnsCache &&
      this.lastRenderData?.table === data.table &&
      this.lastRenderData?.sourceHandles === data.sourceHandles &&
      this.lastRenderData?.targetHandles === data.targetHandles
    ) {
      return this.columnsCache;
    }

    this.lastRenderData = { table: data.table, sourceHandles: data.sourceHandles, targetHandles: data.targetHandles };

    const sortedColumns = this.getSortedColumns();

    this.columnsCache = sortedColumns.map((col: any, idx: number) => {
      const isBaseEntity = this.baseEntityColumns.includes(col.name);
      const dataType = isBaseEntity ? this.getBaseEntityType(col.name) : col.kuikaDataType;
      const isGuidValue = typeof dataType === "string" && GUID_PATTERN.test(dataType);
      const labelType = isGuidValue ? "String" : dataType;
      const handleId = `${data.table.name}-${col.name}`;
      const isSource = data.sourceHandles?.has(handleId);
      const isTarget = data.targetHandles?.has(handleId);

      return (
        <div
          key={col.id || idx}
          className={`table-node-column${col.isPk ? " is-pk" : ""}${isSource ? " is-fk" : ""}`}
          data-testid={`DiagramTableNodeColumn-${data.table.name}-${col.name}`}
        >
          {isTarget && (
            <Handle
              type="target"
              position={Position.Left}
              id={handleId}
              className="column-handle column-handle-target"
            />
          )}
          {isSource && (
            <Handle
              type="source"
              position={Position.Right}
              id={handleId}
              className="column-handle column-handle-source"
            />
          )}
          {/* Fixed-width gutter so every column name starts on one left edge —
              today the icon width shifts with the label and the eye cannot scan down. */}
          <span className="table-node-column__gutter">
            {col.isPk && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="8" cy="15" r="4" />
                <path d="M10.85 12.15 19 4" />
                <path d="m18 5 2 2" />
                <path d="m15 8 2 2" />
              </svg>
            )}
            {!col.isPk && isSource && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
                <path d="M8 12h8" />
              </svg>
            )}
          </span>
          <span className="table-node-column__name">{col.name}</span>
          <span className="table-node-column__type">{labelType}</span>
        </div>
      );
    });

    return this.columnsCache;
  };

  render() {
    const { data } = this.props;
    const isClickable: boolean = data.isClickable !== undefined ? data.isClickable : true;
    const columns: any[] = this.getSortedColumns();
    const schema: string = data.table.schema || "dbo";

    return (
      <div
        className={`react-flow-table-node${isClickable ? " clickable" : ""}`}
        id={`classid-${data.table.name}`}
        data-testid={`DiagramTableNode-${data.table.name}`}
        style={data.width ? { width: data.width } : undefined}
      >
        <Handle type="target" position={Position.Top} className="table-node-handle-hidden" />
        <Handle type="source" position={Position.Bottom} className="table-node-handle-hidden" />

        <div className="table-node-header">
          <span className="table-node-header__accent" />
          <span className="table-node-header__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2.5" />
              <path d="M3 9h18M9 9v12" />
            </svg>
          </span>
          <span className="table-node-header__name">
            <span className="table-node-header__schema">{schema}</span>
            {/* The E2E suite asserts this element's text equals the table name
                exactly (DataDiagramTableNodePage), so the id belongs on the title
                itself — the header row now also holds the schema and the count. */}
            <span className="table-node-header__title" data-testid={`DiagramTableNodeHeader-${data.table.name}`}>
              {data.table.name}
            </span>
          </span>
          {!isClickable && (
            <span className="table-node-header__lock" title="Platform table — read only">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <rect x="4" y="11" width="16" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
            </span>
          )}
          <span className="table-node-header__count">{columns.length}</span>
        </div>
        <div className="table-node-body">{this.renderColumns()}</div>
      </div>
    );
  }
}
